import Box from 'components/shared/Box'
import { FamilyButton, FamilySection } from './styles'
import Text from 'components/shared/Text'
import Icon from 'components/shared/Icon'
import familyIcon from 'assets/icons/family.svg'
import { FormattedMessage, useIntl } from 'react-intl'
import { useResolutions } from 'store/resolutions'
import { FamilyPackageElement } from 'components/global/Family/components/FamilyPackageElement'
import { FamilyPackageSummary } from 'components/global/Family/components/FamilyPackageSummary'
import Loader from 'components/shared/Loader'
import {
    CartItemTicketDetailDto,
    GetCartItemPresenter,
} from 'store/api-slice/hooks'
import { PRODUCT_TYPE_TICKET, PRODUCT_TYPE_VOUCHER } from 'constants/app'
import { formatPrice } from 'helpers/helpers'
import { useEffect } from 'react'
import { useAutoScroll } from 'utils/hooks'
import { useTickets } from 'store/ticket-slice/hooks'
import { useFamilyPackage } from 'components/global/Family/components/FamilyPackage/useFamilyPackage'
import { DEFAULT_LANG } from 'constants/i18n'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'

export type LengthUnion = 1

interface FamilyPackageInterface {
    data: GetCartItemPresenter[]
}

export const FamilyPackage = ({ data }: FamilyPackageInterface) => {
    const { formatMessage, locale } = useIntl()
    const { isMobile } = useResolutions()
    const { removeFamilyPackageFromCart, isFamilyPackageProcessing } =
        useTickets()
    const { summary, items } = useFamilyPackage(data)
    const { animatePendingScroll, calculateScroll, scrollIndex } =
        useAutoScroll()
    const { isBlackPromoForTickets } = usePromotions()

    const { totalDays, totalPrice } = summary

    useEffect(() => {
        animatePendingScroll('special-offer')
    }, [scrollIndex])

    useEffect(
        () => () => {
            calculateScroll({
                trigerringSection: 'tickets',
                trigerringAction: 'delete_offer',
            })
        },
        []
    )

    return (
        <FamilySection id="special-offer">
            <Box
                direction="row"
                align="center"
                justify="space-between"
                isFullWidth
            >
                <Box direction="column" align="flex-start" gap={8}>
                    <Box direction="row" gap={8}>
                        <Text variant="heading2" color="grayscale_900">
                            <FormattedMessage
                                id="package.family.title"
                                defaultMessage="Pakiet Family"
                            />
                        </Text>
                        {!isMobile && (
                            <img src={familyIcon} alt="Family Icon" />
                        )}
                    </Box>
                    {isBlackPromoForTickets && (
                        <Text variant="bodySmall" color="grayscale_800">
                            <FormattedMessage
                                id="package.family.not_combine_with_bw"
                                defaultMessage="Nie łączy się z promocją Black Weekend."
                            />
                        </Text>
                    )}
                </Box>
                {isFamilyPackageProcessing ? (
                    <Loader />
                ) : (
                    <FamilyButton
                        onClick={() => removeFamilyPackageFromCart()}
                        tabIndex={0}
                        as="a"
                        data-testid="package-button-delete"
                    >
                        <FormattedMessage
                            id="global.btn.remove"
                            defaultMessage="Usuń"
                        />
                        <Icon variant="bin" />
                    </FamilyButton>
                )}
            </Box>
            <Text variant="bodySmall" color="grayscale_700">
                {totalDays > 1 && (
                    <FormattedMessage
                        id="package.family.tickets_days"
                        defaultMessage="Całodniowe bilety na {days} dni"
                        values={{
                            days: totalDays,
                        }}
                    />
                )}
            </Text>
            <Box mt={[32, 38]} pb={[24, 40]} isFullWidth gap={16}>
                {items.map((item) => {
                    const {
                        productType,
                        quantity,
                        name,
                        unitPrice,
                        detail,
                        description,
                        cartItemId,
                    } = item
                    const standarizedQuantity = quantity || 0
                    const isTicket = productType === PRODUCT_TYPE_TICKET
                    const isVoucher = productType === PRODUCT_TYPE_VOUCHER

                    if (isTicket) {
                        const { zone } = detail as CartItemTicketDetailDto
                        const nonNullableDescription = description || ''
                        const descriptionMatch =
                            nonNullableDescription.match(/[„"](.*?)[”"]/)
                        const trimmedDescription = descriptionMatch
                            ? descriptionMatch[1]
                            : ''

                        return (
                            <FamilyPackageElement
                                type="ticket"
                                quantity={standarizedQuantity}
                                length={totalDays}
                                description={formatMessage(
                                    {
                                        id: 'package.family.ticket_name',
                                        defaultMessage: 'Bilet {name}',
                                    },
                                    {
                                        name:
                                            locale === DEFAULT_LANG
                                                ? (name ?? '').toLowerCase()
                                                : name ?? '',
                                    }
                                )}
                                zone={zone}
                                time={trimmedDescription}
                                key={cartItemId}
                            />
                        )
                    }
                    if (isVoucher)
                        return (
                            <FamilyPackageElement
                                type="voucher"
                                quantity={totalDays}
                                description={formatMessage(
                                    {
                                        id: 'package.family.voucher_value',
                                        defaultMessage:
                                            'Voucher o wartości {value} zł',
                                    },
                                    {
                                        value: formatPrice(unitPrice || 0),
                                    }
                                )}
                                key={cartItemId}
                            />
                        )
                    return null
                })}
            </Box>
            <FamilyPackageSummary numberOfDays={totalDays} price={totalPrice} />
        </FamilySection>
    )
}
